package com.fantasytenis.web.screens.settings

import androidx.compose.runtime.*
import com.fantasytenis.shared.core.utils.SharedData
import com.fantasytenis.shared.data.auth.dto.PresetAvatarDto
import com.fantasytenis.web.components.*
import com.fantasytenis.web.di.DependencyContainer
import com.fantasytenis.web.theme.Colors
import com.fantasytenis.web.theme.borderTop
import com.fantasytenis.web.theme.h2Style
import com.fantasytenis.web.theme.textTransform
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.Job
import kotlinx.coroutines.await
import kotlinx.coroutines.delay
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.accept
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLInputElement
import org.w3c.files.File
import org.w3c.files.FileReader
import org.w3c.files.get
import org.w3c.xhr.XMLHttpRequest

/**
 * Account Settings Screen - Edit profile, username, avatar.
 */
@Composable
fun AccountSettingsScreen(
    onNavigateBack: () -> Unit
) {
    val scope = rememberCoroutineScope()
    val currentUser by SharedData.currentUser.collectAsState()

    // Form state - use currentUser?.id as key to reinitialize when user data loads
    var firstName by remember(currentUser?.id) { mutableStateOf(currentUser?.firstName ?: "") }
    var lastName by remember(currentUser?.id) { mutableStateOf(currentUser?.lastName ?: "") }
    var username by remember(currentUser?.id) { mutableStateOf(currentUser?.username ?: "") }
    var phoneNumber by remember(currentUser?.id) { mutableStateOf(currentUser?.phoneNumber ?: "") }

    // UI state
    var isSaving by remember { mutableStateOf(false) }
    var error by remember { mutableStateOf<String?>(null) }
    var successMessage by remember { mutableStateOf<String?>(null) }
    var showAvatarPicker by remember { mutableStateOf(false) }

    // Username validation
    var usernameCheckJob by remember { mutableStateOf<Job?>(null) }
    var usernameAvailable by remember { mutableStateOf<Boolean?>(null) }
    var usernameChecking by remember { mutableStateOf(false) }
    var usernameSuggestion by remember { mutableStateOf<String?>(null) }

    // Track if username changed from original
    val originalUsername = currentUser?.username ?: ""
    val usernameChanged = username.lowercase() != originalUsername.lowercase()

    // Validate username when changed
    LaunchedEffect(username) {
        if (!usernameChanged || username.isBlank()) {
            usernameAvailable = null
            usernameChecking = false
            return@LaunchedEffect
        }

        usernameCheckJob?.cancel()
        usernameChecking = true
        usernameCheckJob = scope.launch {
            delay(500) // Debounce
            DependencyContainer.authRepository.checkUsernameAvailable(username.lowercase())
                .onSuccess { result ->
                    usernameAvailable = result.available
                    usernameSuggestion = result.suggestion.takeIf { it.isNotBlank() }
                    usernameChecking = false
                }
                .onFailure {
                    usernameChecking = false
                }
        }
    }

    // Check if form has changes
    val hasChanges = currentUser?.let { user ->
        firstName != user.firstName ||
                lastName != user.lastName ||
                username.lowercase() != (user.username ?: "").lowercase() ||
                phoneNumber != (user.phoneNumber ?: "")
    } ?: false

    // Validate form
    val isFormValid = firstName.isNotBlank() &&
            lastName.isNotBlank() &&
            (!usernameChanged || usernameAvailable == true || !usernameChecking)

    fun saveChanges() {
        if (!isFormValid || isSaving) return

        isSaving = true
        error = null
        successMessage = null

        scope.launch {
            DependencyContainer.authRepository.updateUser(
                firstName = firstName.takeIf { it != currentUser?.firstName },
                lastName = lastName.takeIf { it != currentUser?.lastName },
                username = username.lowercase().takeIf { usernameChanged },
                phoneNumber = phoneNumber.takeIf { it != currentUser?.phoneNumber }
            ).onSuccess { updatedUser ->
                SharedData.currentUser.value = updatedUser
                isSaving = false
                successMessage = "Changes saved successfully"
                usernameAvailable = null // Reset since it's now the current username
            }.onFailure { e ->
                isSaving = false
                error = e.message ?: "Failed to save changes"
            }
        }
    }

    Div({
        style {
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            padding(32.px)
            maxWidth(600.px)
            margin(0.px, "auto".unsafeCast<CSSNumeric>())
        }
    }) {
        // Header
        Div({
            style {
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(16.px)
                marginBottom(32.px)
            }
        }) {
            // Back button
            A(
                href = "#",
                attrs = {
                    onClick { e ->
                        e.preventDefault()
                        onNavigateBack()
                    }
                    style {
                        color(Colors.TextSecondary)
                        fontSize(24.px)
                        property("text-decoration", "none")
                    }
                }
            ) {
                Text("\u2190")
            }
            H2({
                style {
                    h2Style()
                    margin(0.px)
                }
            }) {
                Text("Account Settings")
            }
        }

        currentUser?.let { user ->
            // Avatar Hero Section
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.Center)
                    marginBottom(40.px)
                    padding(24.px)
                    backgroundColor(Colors.Surface)
                    borderRadius(16.px)
                }
            }) {
                // Large avatar with hover effect
                Div({
                    style {
                        position(Position.Relative)
                        cursor("pointer")
                    }
                    onClick { showAvatarPicker = true }
                }) {
                    AvatarDisplay(
                        avatarUrl = user.avatarUrl,
                        initials = user.initials,
                        size = AvatarSize.XLARGE,
                        onClick = { showAvatarPicker = true }
                    )
                    // Camera overlay on hover
                    Div({
                        style {
                            position(Position.Absolute)
                            top(0.px)
                            left(0.px)
                            right(0.px)
                            bottom(0.px)
                            borderRadius(50.percent)
                            backgroundColor(rgba(0, 0, 0, 0.5))
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            opacity(0)
                            property("transition", "opacity 0.2s ease")
                        }
                        classes("avatar-overlay")
                    }) {
                        Span({
                            style {
                                fontSize(32.px)
                                color(Color.white)
                            }
                        }) {
                            Text("\uD83D\uDCF7") // Camera emoji
                        }
                    }
                }

                // Edit button
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(12.px)
                        marginTop(16.px)
                    }
                }) {
                    A(
                        href = "#",
                        attrs = {
                            onClick { e ->
                                e.preventDefault()
                                showAvatarPicker = true
                            }
                            style {
                                color(Colors.Primary)
                                fontSize(14.px)
                                fontWeight(500)
                                property("text-decoration", "none")
                            }
                        }
                    ) {
                        Text("Change Avatar")
                    }
                    if (user.avatarUrl != null) {
                        Span({ style { color(Colors.Border) } }) { Text("|") }
                        A(
                            href = "#",
                            attrs = {
                                onClick { e ->
                                    e.preventDefault()
                                    scope.launch {
                                        DependencyContainer.authRepository.updateUser(
                                            avatarType = "none",
                                            avatarPresetId = null,
                                            avatarUrl = null
                                        ).onSuccess { updatedUser ->
                                            SharedData.currentUser.value = updatedUser
                                        }
                                    }
                                }
                                style {
                                    color(Colors.TextSecondary)
                                    fontSize(14.px)
                                    property("text-decoration", "none")
                                }
                            }
                        ) {
                            Text("Remove")
                        }
                    }
                }
            }

            // Profile Information Section
            Div({
                style {
                    marginBottom(24.px)
                }
            }) {
                P({
                    style {
                        fontSize(12.px)
                        fontWeight(600)
                        color(Colors.TextSecondary)
                        marginBottom(16.px)
                        textTransform("uppercase")
                        property("letter-spacing", "0.5px")
                    }
                }) {
                    Text("Profile Information")
                }

                Div({
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Column)
                        gap(16.px)
                    }
                }) {
                    // Name fields
                    Div({
                        style {
                            display(DisplayStyle.Flex)
                            gap(16.px)
                        }
                    }) {
                        Div({ style { flex(1) } }) {
                            TextField(
                                value = firstName,
                                onValueChange = { firstName = it },
                                label = "First Name"
                            )
                        }
                        Div({ style { flex(1) } }) {
                            TextField(
                                value = lastName,
                                onValueChange = { lastName = it },
                                label = "Last Name"
                            )
                        }
                    }

                    // Username field
                    Div {
                        TextField(
                            value = username,
                            onValueChange = { username = it.lowercase().filter { c -> c.isLetterOrDigit() || c == '_' } },
                            label = "Username",
                            placeholder = "johndoe"
                        )

                        // Username status
                        if (username.isNotBlank() && usernameChanged) {
                            Div({
                                style {
                                    marginTop(4.px)
                                    fontSize(12.px)
                                }
                            }) {
                                when {
                                    usernameChecking -> {
                                        Span({ style { color(Colors.TextSecondary) } }) {
                                            Text("Checking availability...")
                                        }
                                    }
                                    usernameAvailable == true -> {
                                        Span({ style { color(Colors.Success) } }) {
                                            Text("\u2713 @$username is available")
                                        }
                                    }
                                    usernameAvailable == false -> {
                                        Span({ style { color(Colors.Error) } }) {
                                            Text("\u2717 Username is taken")
                                        }
                                        usernameSuggestion?.let { suggestion ->
                                            Span({ style { color(Colors.TextSecondary); marginLeft(8.px) } }) {
                                                Text("Try: ")
                                                A(
                                                    href = "#",
                                                    attrs = {
                                                        onClick { e ->
                                                            e.preventDefault()
                                                            username = suggestion
                                                        }
                                                        style { color(Colors.Accent) }
                                                    }
                                                ) {
                                                    Text("@$suggestion")
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }

                    // Phone number
                    TextField(
                        value = phoneNumber,
                        onValueChange = { phoneNumber = it },
                        label = "Phone Number (optional)",
                        placeholder = "+1 555 123 4567"
                    )

                    // Email (read-only)
                    Div {
                        Label(attrs = {
                            style {
                                display(DisplayStyle.Block)
                                marginBottom(6.px)
                                fontSize(14.px)
                                fontWeight(500)
                                color(Colors.TextSecondary)
                            }
                        }) {
                            Text("Email")
                        }
                        Div({
                            style {
                                padding(12.px, 16.px)
                                backgroundColor(Colors.SurfaceVariant)
                                borderRadius(8.px)
                                fontSize(14.px)
                                color(Colors.TextSecondary)
                            }
                        }) {
                            Text(user.email)
                        }
                        Span({
                            style {
                                fontSize(12.px)
                                color(Colors.TextSecondary)
                                marginTop(4.px)
                                display(DisplayStyle.Block)
                            }
                        }) {
                            Text("Email cannot be changed")
                        }
                    }
                }
            }

            // Error/Success messages
            error?.let { errorMessage ->
                P({
                    style {
                        color(Colors.Error)
                        fontSize(14.px)
                        marginBottom(16.px)
                    }
                }) {
                    Text(errorMessage)
                }
            }

            successMessage?.let { message ->
                P({
                    style {
                        color(Colors.Success)
                        fontSize(14.px)
                        marginBottom(16.px)
                    }
                }) {
                    Text(message)
                }
            }

            // Save button
            Button(
                text = if (isSaving) "Saving..." else "Save Changes",
                onClick = { saveChanges() },
                enabled = hasChanges && isFormValid && !isSaving,
                fullWidth = true
            )

            // Divider
            Div({
                style {
                    height(1.px)
                    backgroundColor(Colors.Border)
                    margin(32.px, 0.px)
                }
            }) {}

            // Other settings links
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    gap(8.px)
                }
            }) {
                SettingsLink(
                    text = "Change Password",
                    onClick = { /* TODO: Navigate to change password */ }
                )
                SettingsLink(
                    text = "Notification Preferences",
                    onClick = { /* TODO: Navigate to notification settings */ }
                )
            }
        }
    }

    // Avatar Picker Dialog
    if (showAvatarPicker) {
        AvatarPickerDialog(
            currentAvatarUrl = currentUser?.avatarUrl,
            currentInitials = currentUser?.initials ?: "?",
            onDismiss = { showAvatarPicker = false },
            onAvatarSelected = { avatarType, presetId, avatarUrl ->
                showAvatarPicker = false
                scope.launch {
                    DependencyContainer.authRepository.updateUser(
                        avatarType = avatarType,
                        avatarPresetId = presetId,
                        avatarUrl = avatarUrl
                    ).onSuccess { updatedUser ->
                        SharedData.currentUser.value = updatedUser
                    }
                }
            }
        )
    }

    // Inject hover styles
    LaunchedEffect(Unit) {
        val style = document.createElement("style")
        style.textContent = """
            .avatar-overlay:hover {
                opacity: 1 !important;
            }
        """.trimIndent()
        document.head?.appendChild(style)
    }
}

@Composable
private fun SettingsLink(
    text: String,
    onClick: () -> Unit
) {
    A(
        href = "#",
        attrs = {
            onClick { e ->
                e.preventDefault()
                onClick()
            }
            style {
                display(DisplayStyle.Flex)
                justifyContent(JustifyContent.SpaceBetween)
                alignItems(AlignItems.Center)
                padding(16.px)
                backgroundColor(Colors.Surface)
                borderRadius(8.px)
                color(Colors.TextPrimary)
                property("text-decoration", "none")
                fontSize(14.px)
            }
        }
    ) {
        Text(text)
        Span({ style { color(Colors.TextSecondary) } }) {
            Text("\u203A")
        }
    }
}

/**
 * Upload state for avatar picker.
 */
private sealed class UploadState {
    object Idle : UploadState()
    data class Selected(val file: File, val previewUrl: String) : UploadState()
    data class Uploading(val progress: Int) : UploadState()
    data class Success(val avatarUrl: String) : UploadState()
    data class Error(val message: String) : UploadState()
}

/**
 * Avatar Picker Dialog - Select preset or upload custom avatar.
 */
@Composable
private fun AvatarPickerDialog(
    currentAvatarUrl: String?,
    currentInitials: String,
    onDismiss: () -> Unit,
    onAvatarSelected: (avatarType: String, presetId: String?, avatarUrl: String?) -> Unit
) {
    val scope = rememberCoroutineScope()
    var presets by remember { mutableStateOf<List<PresetAvatarDto>>(emptyList()) }
    var isLoading by remember { mutableStateOf(true) }
    var selectedTab by remember { mutableStateOf("presets") }
    var previewAvatarUrl by remember { mutableStateOf(currentAvatarUrl) }
    var uploadState by remember { mutableStateOf<UploadState>(UploadState.Idle) }

    // Load presets
    LaunchedEffect(Unit) {
        DependencyContainer.authRepository.getPresetAvatars()
            .onSuccess { data ->
                presets = data.avatars
                isLoading = false
            }
            .onFailure {
                isLoading = false
            }
    }

    // Dialog overlay
    Div({
        style {
            position(Position.Fixed)
            top(0.px)
            left(0.px)
            right(0.px)
            bottom(0.px)
            backgroundColor(rgba(0, 0, 0, 0.5))
            display(DisplayStyle.Flex)
            alignItems(AlignItems.Center)
            justifyContent(JustifyContent.Center)
            property("z-index", "1000")
        }
        onClick { onDismiss() }
    }) {
        // Dialog content
        Div({
            style {
                backgroundColor(Colors.Background)
                borderRadius(16.px)
                padding(24.px)
                width(440.px)
                maxHeight(85.vh)
                overflowY("auto")
                property("box-shadow", "0 20px 60px rgba(0, 0, 0, 0.3)")
            }
            onClick { it.stopPropagation() }
        }) {
            // Header
            Div({
                style {
                    display(DisplayStyle.Flex)
                    justifyContent(JustifyContent.SpaceBetween)
                    alignItems(AlignItems.Center)
                    marginBottom(20.px)
                }
            }) {
                H3({
                    style {
                        margin(0.px)
                        fontSize(20.px)
                        fontWeight(600)
                    }
                }) {
                    Text("Choose Avatar")
                }
                A(
                    href = "#",
                    attrs = {
                        onClick { e ->
                            e.preventDefault()
                            onDismiss()
                        }
                        style {
                            color(Colors.TextSecondary)
                            fontSize(24.px)
                            property("text-decoration", "none")
                            width(32.px)
                            height(32.px)
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            borderRadius(50.percent)
                            property("transition", "background-color 0.2s")
                        }
                    }
                ) {
                    Text("\u00D7")
                }
            }

            // Live Preview Section
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.Center)
                    padding(20.px)
                    backgroundColor(Colors.Surface)
                    borderRadius(12.px)
                    marginBottom(20.px)
                }
            }) {
                AvatarDisplay(
                    avatarUrl = previewAvatarUrl,
                    initials = currentInitials,
                    size = AvatarSize.LARGE
                )
                P({
                    style {
                        margin(8.px, 0.px, 0.px, 0.px)
                        fontSize(12.px)
                        color(Colors.TextSecondary)
                    }
                }) {
                    Text(if (previewAvatarUrl != null) "Preview" else "Using initials")
                }
            }

            // Category Tabs
            Div({
                style {
                    display(DisplayStyle.Flex)
                    gap(8.px)
                    marginBottom(20.px)
                    padding(4.px)
                    backgroundColor(Colors.SurfaceVariant)
                    borderRadius(10.px)
                }
            }) {
                CategoryTab("Tennis", "tennis", selectedTab == "tennis") {
                    selectedTab = "tennis"
                    uploadState = UploadState.Idle
                }
                CategoryTab("Trophies", "achievement", selectedTab == "achievement") {
                    selectedTab = "achievement"
                    uploadState = UploadState.Idle
                }
                CategoryTab("Colors", "abstract", selectedTab == "abstract") {
                    selectedTab = "abstract"
                    uploadState = UploadState.Idle
                }
                CategoryTab("Upload", "upload", selectedTab == "upload") {
                    selectedTab = "upload"
                }
            }

            // Content based on selected tab
            when (selectedTab) {
                "tennis", "achievement", "abstract" -> {
                    if (isLoading) {
                        // Loading skeleton
                        Div({
                            style {
                                display(DisplayStyle.Grid)
                                gridTemplateColumns("repeat(4, 1fr)")
                                gap(12.px)
                            }
                        }) {
                            repeat(8) {
                                Div({
                                    style {
                                        width(70.px)
                                        height(70.px)
                                        borderRadius(50.percent)
                                        backgroundColor(Colors.SurfaceVariant)
                                        property("animation", "pulse 1.5s ease-in-out infinite")
                                    }
                                }) {}
                            }
                        }
                    } else {
                        val categoryAvatars = presets.filter { it.category == selectedTab }

                        // Avatar grid
                        Div({
                            style {
                                display(DisplayStyle.Grid)
                                gridTemplateColumns("repeat(4, 1fr)")
                                gap(12.px)
                            }
                        }) {
                            categoryAvatars.forEach { avatar ->
                                Div({
                                    style {
                                        width(70.px)
                                        height(70.px)
                                        borderRadius(50.percent)
                                        overflow("hidden")
                                        cursor("pointer")
                                        border(
                                            3.px,
                                            LineStyle.Solid,
                                            if (avatar.url == currentAvatarUrl) Colors.Primary else Color.transparent
                                        )
                                        property("transition", "transform 0.2s, border-color 0.2s")
                                        property("box-shadow", if (avatar.url == currentAvatarUrl) "0 0 0 3px rgba(0, 166, 81, 0.2)" else "none")
                                    }
                                    onMouseOver {
                                        previewAvatarUrl = avatar.url
                                    }
                                    onMouseOut {
                                        previewAvatarUrl = currentAvatarUrl
                                    }
                                    onClick {
                                        onAvatarSelected("preset", avatar.id, avatar.url)
                                    }
                                }) {
                                    Img(
                                        src = avatar.url,
                                        alt = avatar.id,
                                        attrs = {
                                            style {
                                                width(100.percent)
                                                height(100.percent)
                                                property("object-fit", "cover")
                                            }
                                        }
                                    )
                                }
                            }
                        }
                    }
                }
                "upload" -> {
                    UploadTab(
                        uploadState = uploadState,
                        onFileSelected = { file, previewUrl ->
                            uploadState = UploadState.Selected(file, previewUrl)
                            previewAvatarUrl = previewUrl
                        },
                        onUpload = { file ->
                            scope.launch {
                                uploadState = UploadState.Uploading(0)

                                // Get signed upload URL
                                DependencyContainer.authRepository.getAvatarUploadUrl(file.type)
                                    .onSuccess { uploadData ->
                                        // Upload to GCS
                                        try {
                                            uploadFileToGcs(
                                                uploadUrl = uploadData.uploadUrl,
                                                file = file,
                                                onProgress = { progress ->
                                                    uploadState = UploadState.Uploading(progress)
                                                }
                                            )

                                            uploadState = UploadState.Success(uploadData.publicUrl)

                                            // Auto-select the uploaded avatar
                                            delay(500) // Brief delay to show success
                                            onAvatarSelected("custom", null, uploadData.publicUrl)
                                        } catch (e: Exception) {
                                            uploadState = UploadState.Error(e.message ?: "Upload failed")
                                        }
                                    }
                                    .onFailure { e ->
                                        uploadState = UploadState.Error(e.message ?: "Failed to get upload URL")
                                    }
                            }
                        },
                        onCancel = {
                            uploadState = UploadState.Idle
                            previewAvatarUrl = currentAvatarUrl
                        },
                        onRetry = {
                            uploadState = UploadState.Idle
                            previewAvatarUrl = currentAvatarUrl
                        }
                    )
                }
            }

            // Remove avatar option
            Div({
                style {
                    marginTop(20.px)
                    paddingTop(16.px)
                    borderTop(1.px, LineStyle.Solid, Colors.Border)
                    textAlign("center")
                }
            }) {
                A(
                    href = "#",
                    attrs = {
                        onClick { e ->
                            e.preventDefault()
                            onAvatarSelected("none", null, null)
                        }
                        style {
                            color(Colors.TextSecondary)
                            fontSize(14.px)
                            property("text-decoration", "none")
                        }
                    }
                ) {
                    Text("Use Initials Instead")
                }
            }
        }
    }

    // Inject animation styles
    LaunchedEffect(Unit) {
        val style = document.createElement("style")
        style.textContent = """
            @keyframes pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.5; }
            }
        """.trimIndent()
        document.head?.appendChild(style)
    }
}

@Composable
private fun CategoryTab(
    label: String,
    value: String,
    isSelected: Boolean,
    onClick: () -> Unit
) {
    Div({
        style {
            flex(1)
            padding(8.px, 12.px)
            textAlign("center")
            fontSize(13.px)
            fontWeight(if (isSelected) 600 else 400)
            color(if (isSelected) Color.white else Colors.TextSecondary)
            backgroundColor(if (isSelected) Colors.Primary else Color.transparent)
            borderRadius(8.px)
            cursor("pointer")
            property("transition", "all 0.2s ease")
        }
        onClick { onClick() }
    }) {
        Text(label)
    }
}

@Composable
private fun UploadTab(
    uploadState: UploadState,
    onFileSelected: (File, String) -> Unit,
    onUpload: (File) -> Unit,
    onCancel: () -> Unit,
    onRetry: () -> Unit
) {
    var isDragging by remember { mutableStateOf(false) }
    var validationError by remember { mutableStateOf<String?>(null) }

    fun validateAndSelectFile(file: File) {
        validationError = null

        // Validate file type
        val validTypes = listOf("image/jpeg", "image/png", "image/webp")
        if (file.type !in validTypes) {
            validationError = "Invalid file type. Use JPG, PNG, or WebP."
            return
        }

        // Validate file size (5MB)
        if (file.size.toLong() > 5 * 1024 * 1024) {
            validationError = "File too large. Maximum size is 5MB."
            return
        }

        // Create preview
        val reader = FileReader()
        reader.onload = { event ->
            val dataUrl = reader.result as String
            onFileSelected(file, dataUrl)
        }
        reader.readAsDataURL(file)
    }

    when (uploadState) {
        is UploadState.Idle -> {
            // Drop zone
            Div({
                style {
                    border(2.px, LineStyle.Dashed, if (isDragging) Colors.Primary else Colors.Border)
                    borderRadius(12.px)
                    padding(40.px)
                    textAlign("center")
                    backgroundColor(if (isDragging) rgba(0, 166, 81, 0.05) else Color.transparent)
                    cursor("pointer")
                    property("transition", "all 0.2s ease")
                }
                onDragOver { e ->
                    e.preventDefault()
                    isDragging = true
                }
                onDragLeave {
                    isDragging = false
                }
                onDrop { e ->
                    e.preventDefault()
                    isDragging = false
                    val file = e.dataTransfer?.files?.get(0)
                    if (file != null) {
                        validateAndSelectFile(file)
                    }
                }
                onClick {
                    // Trigger file input
                    val input = document.createElement("input") as HTMLInputElement
                    input.type = "file"
                    input.accept = "image/jpeg,image/png,image/webp"
                    input.onchange = {
                        val file = input.files?.get(0)
                        if (file != null) {
                            validateAndSelectFile(file)
                        }
                    }
                    input.click()
                }
            }) {
                Div({
                    style {
                        fontSize(48.px)
                        marginBottom(12.px)
                    }
                }) {
                    Text(if (isDragging) "\uD83D\uDCE5" else "\uD83D\uDCF7") // Inbox or camera emoji
                }
                P({
                    style {
                        margin(0.px, 0.px, 8.px, 0.px)
                        fontSize(14.px)
                        fontWeight(500)
                        color(Colors.TextPrimary)
                    }
                }) {
                    Text(if (isDragging) "Drop your image here" else "Drag & drop or click to browse")
                }
                P({
                    style {
                        margin(0.px)
                        fontSize(12.px)
                        color(Colors.TextSecondary)
                    }
                }) {
                    Text("JPG, PNG, or WebP \u2022 Max 5MB")
                }
            }

            // Validation error
            validationError?.let { error ->
                P({
                    style {
                        color(Colors.Error)
                        fontSize(13.px)
                        marginTop(12.px)
                        textAlign("center")
                    }
                }) {
                    Text(error)
                }
            }
        }

        is UploadState.Selected -> {
            // Preview with upload button
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.Center)
                    gap(16.px)
                }
            }) {
                // Preview image
                Div({
                    style {
                        width(120.px)
                        height(120.px)
                        borderRadius(50.percent)
                        overflow("hidden")
                        border(3.px, LineStyle.Solid, Colors.Primary)
                    }
                }) {
                    Img(
                        src = uploadState.previewUrl,
                        alt = "Preview",
                        attrs = {
                            style {
                                width(100.percent)
                                height(100.percent)
                                property("object-fit", "cover")
                            }
                        }
                    )
                }

                P({
                    style {
                        margin(0.px)
                        fontSize(13.px)
                        color(Colors.TextSecondary)
                    }
                }) {
                    Text(uploadState.file.name)
                }

                // Action buttons
                Div({
                    style {
                        display(DisplayStyle.Flex)
                        gap(12.px)
                    }
                }) {
                    Button(
                        text = "Cancel",
                        onClick = onCancel,
                        variant = "secondary"
                    )
                    Button(
                        text = "Upload",
                        onClick = { onUpload(uploadState.file) }
                    )
                }
            }
        }

        is UploadState.Uploading -> {
            // Upload progress
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.Center)
                    gap(16.px)
                    padding(32.px)
                }
            }) {
                // Tennis ball spinner
                Div({
                    style {
                        fontSize(48.px)
                        property("animation", "spin 1s linear infinite")
                    }
                }) {
                    Text("\uD83C\uDFBE") // Tennis ball emoji
                }

                P({
                    style {
                        margin(0.px)
                        fontSize(14.px)
                        color(Colors.TextPrimary)
                    }
                }) {
                    Text("Uploading...")
                }

                // Progress bar
                Div({
                    style {
                        width(200.px)
                        height(4.px)
                        backgroundColor(Colors.SurfaceVariant)
                        borderRadius(2.px)
                        overflow("hidden")
                    }
                }) {
                    Div({
                        style {
                            width(uploadState.progress.percent)
                            height(100.percent)
                            backgroundColor(Colors.Primary)
                            property("transition", "width 0.3s ease")
                        }
                    }) {}
                }

                P({
                    style {
                        margin(0.px)
                        fontSize(12.px)
                        color(Colors.TextSecondary)
                    }
                }) {
                    Text("${uploadState.progress}%")
                }
            }
        }

        is UploadState.Success -> {
            // Success state
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.Center)
                    gap(12.px)
                    padding(32.px)
                }
            }) {
                Div({
                    style {
                        fontSize(48.px)
                    }
                }) {
                    Text("\u2705") // Checkmark
                }
                P({
                    style {
                        margin(0.px)
                        fontSize(14.px)
                        fontWeight(500)
                        color(Colors.Success)
                    }
                }) {
                    Text("Upload complete!")
                }
            }
        }

        is UploadState.Error -> {
            // Error state
            Div({
                style {
                    display(DisplayStyle.Flex)
                    flexDirection(FlexDirection.Column)
                    alignItems(AlignItems.Center)
                    gap(16.px)
                    padding(24.px)
                    backgroundColor(rgba(244, 67, 54, 0.1))
                    borderRadius(12.px)
                }
            }) {
                Div({
                    style {
                        fontSize(48.px)
                    }
                }) {
                    Text("\u274C") // X mark
                }
                P({
                    style {
                        margin(0.px)
                        fontSize(14.px)
                        color(Colors.Error)
                        textAlign("center")
                    }
                }) {
                    Text(uploadState.message)
                }
                Button(
                    text = "Try Again",
                    onClick = onRetry
                )
            }
        }
    }

    // Inject spin animation
    LaunchedEffect(Unit) {
        val style = document.createElement("style")
        style.textContent = """
            @keyframes spin {
                from { transform: rotate(0deg); }
                to { transform: rotate(360deg); }
            }
        """.trimIndent()
        document.head?.appendChild(style)
    }
}

/**
 * Upload file to GCS using XMLHttpRequest for progress tracking.
 */
private suspend fun uploadFileToGcs(
    uploadUrl: String,
    file: File,
    onProgress: (Int) -> Unit
) {
    kotlinx.coroutines.suspendCancellableCoroutine<Unit> { continuation ->
        val xhr = XMLHttpRequest()

        xhr.upload.onprogress = { event ->
            if (event.lengthComputable) {
                val progress = ((event.loaded.toDouble() / event.total.toDouble()) * 100).toInt()
                onProgress(progress)
            }
        }

        xhr.onload = {
            if (xhr.status.toInt() in 200..299) {
                continuation.resumeWith(Result.success(Unit))
            } else {
                continuation.resumeWith(Result.failure(Exception("Upload failed: ${xhr.status}")))
            }
        }

        xhr.onerror = {
            continuation.resumeWith(Result.failure(Exception("Network error")))
        }

        xhr.open("PUT", uploadUrl)
        xhr.setRequestHeader("Content-Type", file.type)
        xhr.send(file)

        continuation.invokeOnCancellation {
            xhr.abort()
        }
    }
}
